import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import './DashboardStats.css';

// `scope` is the admin-only company/branch picker from DeliveryManagement, and
// is null for a normal user (the server has already pinned them to their own
// branch). Passing it through is what keeps these totals showing the SAME
// branch the Deliveries tab is showing — without it an admin narrowed to one
// branch flipped here and was given the whole group's numbers.

// Qatar "today" as YYYY-MM-DD — matches the day boundary the server groups on
// (utils/qatarTime.js / a fixed +03:00). Computed from the UTC instant, never
// the browser's local date, so it doesn't shift when the user travels.
const qatarTodayKey = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);

const ageInDays = (dayKey) =>
    Math.round((Date.parse(qatarTodayKey()) - Date.parse(dayKey)) / 86400000);

const formatDayLabel = (dayKey) => {
    const age = ageInDays(dayKey);
    const nice = new Date(dayKey + 'T00:00:00').toLocaleDateString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
    let rel = `${age} days ago`;
    if (age <= 0) rel = 'today';
    else if (age === 1) rel = 'yesterday';
    return { nice, rel, age };
};

const DashboardStats = ({ scope = null }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    // Which day rows in "Pending by Day" are expanded. Seeded once the stats
    // land: every day opens by default except "yesterday" (age 1) so the detail
    // that matters — today's queue and anything genuinely stale — needs no click.
    const [expandedDays, setExpandedDays] = useState(() => new Set());

    const company = scope?.company || '';
    const branch = scope?.branch || '';

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (company) params.company = company;
            if (branch) params.branch = branch;
            const response = await axiosInstance.get('/api/deliveries/dashboard/stats', { params });
            if (response.data.success) {
                const data = response.data.data;
                setStats(data);
                const seed = new Set(
                    (data.pendingByDay || [])
                        .filter((d) => ageInDays(d._id) !== 1)
                        .map((d) => d._id)
                );
                setExpandedDays(seed);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            alert('Failed to fetch dashboard statistics');
        } finally {
            setLoading(false);
        }
    }, [company, branch]);

    // Re-runs when the admin changes branch — the old empty dep array meant the
    // numbers stayed on whatever branch was selected the first time.
    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) {
        return (
            <div className="ds-loading">
                <div className="spinner"></div>
                Loading dashboard...
            </div>
        );
    }

    if (!stats) {
        return <div className="ds-error">Failed to load statistics</div>;
    }

    return (
        <div className="ds-container">
            {/* Summary Cards */}
            <div className="ds-summary-grid">
                <div className="ds-card ds-card-primary">
                    <div className="ds-card-icon">📦</div>
                    <div className="ds-card-content">
                        <h3>Total Deliveries</h3>
                        <p className="ds-card-value">{stats.totalInvoices}</p>
                        <span className="ds-card-label">QAR {stats.totalAmount.toLocaleString()}</span>
                    </div>
                </div>

                <div className="ds-card ds-card-warning">
                    <div className="ds-card-icon">⏳</div>
                    <div className="ds-card-content">
                        <h3>Pending</h3>
                        <p className="ds-card-value">{stats.pending.count}</p>
                        <span className="ds-card-label">QAR {stats.pending.amount.toLocaleString()}</span>
                    </div>
                </div>

                <div className="ds-card ds-card-success">
                    <div className="ds-card-icon">✅</div>
                    <div className="ds-card-content">
                        <h3>Delivered Today</h3>
                        <p className="ds-card-value">{stats.deliveredToday.count}</p>
                        <span className="ds-card-label">QAR {stats.deliveredToday.amount.toLocaleString()}</span>
                    </div>
                </div>

                <div className="ds-card ds-card-info">
                    <div className="ds-card-icon">📅</div>
                    <div className="ds-card-content">
                        <h3>Delivered This Month</h3>
                        <p className="ds-card-value">{stats.deliveredThisMonth?.count || 0}</p>
                        <span className="ds-card-label">QAR {(stats.deliveredThisMonth?.amount || 0).toLocaleString()}</span>
                    </div>
                </div>
            </div>

            {/* Pending by Day — aging view of everything still unstarted */}
            {stats.pendingByDay && stats.pendingByDay.length > 0 && (
                <div className="ds-section">
                    <h2 className="ds-section-title">
                        Pending by Day
                        <span className="ds-pbd-hint"> — {stats.pending.count} total, grouped by the day the order was raised</span>
                    </h2>
                    <div className="ds-pbd-list">
                        {stats.pendingByDay.map((day) => {
                            const { nice, rel, age } = formatDayLabel(day._id);
                            const open = expandedDays.has(day._id);
                            const tone = age >= 4 ? 'ds-pbd-old' : age >= 2 ? 'ds-pbd-aging' : '';
                            return (
                                <div key={day._id} className={`ds-pbd-day ${tone}`}>
                                    <button
                                        type="button"
                                        className="ds-pbd-head"
                                        aria-expanded={open}
                                        onClick={() => setExpandedDays((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(day._id)) next.delete(day._id);
                                            else next.add(day._id);
                                            return next;
                                        })}
                                    >
                                        <span className="ds-pbd-caret">{open ? '▾' : '▸'}</span>
                                        <span className="ds-pbd-date">{nice}</span>
                                        <span className="ds-pbd-rel">{rel}</span>
                                        <span className="ds-pbd-count">{day.count} pending</span>
                                        <span className="ds-pbd-amount">QAR {day.totalAmount.toLocaleString()}</span>
                                    </button>
                                    {open && (
                                        <div className="ds-table-wrap ds-pbd-table-wrap">
                                            <table className="ds-table">
                                                <thead>
                                                    <tr>
                                                        <th>Invoice</th>
                                                        <th>Customer</th>
                                                        <th>Zone</th>
                                                        <th>Delivery Boy</th>
                                                        <th>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {day.items.map((it) => (
                                                        <tr key={it.id}>
                                                            <td>{it.invoiceNo || '—'}</td>
                                                            <td><strong>{it.customerName || '—'}</strong></td>
                                                            <td>{it.zone ? `Zone ${it.zone}` : (it.area || '—')}</td>
                                                            <td>{it.deliveryBoy || 'Unassigned'}</td>
                                                            <td>QAR {(it.amount || 0).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* By Status */}
            <div className="ds-section">
                <h2 className="ds-section-title">By Status</h2>
                <div className="ds-table-wrap">
                    <table className="ds-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Count</th>
                                <th>Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.byStatus.map((item) => (
                                <tr key={item._id}>
                                    <td>
                                        <span className={`ds-badge ds-badge-${item._id.toLowerCase().replace(' ', '-')}`}>
                                            {item._id}
                                        </span>
                                    </td>
                                    <td><strong>{item.count}</strong></td>
                                    <td>QAR {item.totalAmount.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* By Delivery Boy */}
            <div className="ds-section">
                <h2 className="ds-section-title">By Delivery Boy</h2>
                <div className="ds-table-wrap">
                    <table className="ds-table">
                        <thead>
                            <tr>
                                <th>Delivery Boy</th>
                                <th>Deliveries</th>
                                <th>Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.byDeliveryBoy.map((item) => (
                                <tr key={item._id}>
                                    <td><strong>{item.deliveryBoy || item._id}</strong></td>
                                    <td>{item.count}</td>
                                    <td>QAR {item.totalAmount.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* By Location (Zone + Street) */}
            <div className="ds-section">
                <h2 className="ds-section-title">By Location (Zone + Street)</h2>
                <div className="ds-table-wrap">
                    <table className="ds-table">
                        <thead>
                            <tr>
                                <th>Location</th>
                                <th>Deliveries</th>
                                <th>Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.byLocation && stats.byLocation.map((item) => (
                                <tr key={item._id}>
                                    <td><strong>{item._id}</strong></td>
                                    <td>{item.count}</td>
                                    <td>QAR {item.totalAmount.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DashboardStats;
